import { vizHost, type RenderVizOutcome } from '@cortex/viz/browser';
import { AlertTriangle, Loader2, Sparkles, Wrench } from 'lucide-react';
import * as React from 'react';

// A model-authored d3 visualization (ADR 0013). The code never runs in this page: the shared
// VizHost sends it with its data to the sandboxed runner (a Worker with a virtual DOM, no network),
// and only the SANITIZED SVG that comes back is shown, in a shadow root so the page's styles and
// the chart's never meet (and its <title> tooltips still work). Nothing is ever taken from storage
// or from someone else's render: every mount draws again, for this viewer.

export interface VizFailure {
  kind: string;
  message: string;
  line?: number;
}

/** The chat message that sends a failed render back to the model. */
export const fixMessage = (title: string, e: VizFailure) =>
  `The visualization "${title}" did not render: ${e.message}${e.line ? ` (line ${e.line})` : ''}. Fix the code and show it again.`;

const SHADOW_CSS = `
:host { display: block; color: inherit; }
svg { display: block; width: 100%; height: auto; max-width: 100%; overflow: visible; font-family: inherit; }
svg text { fill: currentColor; }
`;

export function VizView({
  title,
  code,
  data,
  height,
  onFix,
  testid = 'viz',
  bare = false,
}: {
  title: string;
  code: string;
  data: unknown;
  height?: number;
  onFix?: (message: string) => void;
  testid?: string;
  /** Inside a card that has its own title (the chat's viz-card): no border, padding or caption. */
  bare?: boolean;
}) {
  const box = React.useRef<HTMLDivElement>(null);
  const shadow = React.useRef<ShadowRoot | null>(null);
  const [state, setState] = React.useState<
    | { phase: 'rendering' }
    | { phase: 'error'; error: VizFailure }
    | { phase: 'done'; out: Extract<RenderVizOutcome, { ok: true }> }
  >({ phase: 'rendering' });
  // Re-render only when the code or the data change (not on every parent render).
  const key = React.useMemo(() => `${code}\u0000${JSON.stringify(data ?? null)}`, [code, data]);

  React.useEffect(() => {
    const ac = new AbortController();
    setState({ phase: 'rendering' });
    const width = Math.round(box.current?.clientWidth || 640);
    void vizHost()
      .render({ code, data, width, ...(height ? { height } : {}), signal: ac.signal })
      .then((out) => {
        if (ac.signal.aborted) return;
        if (!out.ok)
          return setState({
            phase: 'error',
            error: { kind: out.kind, message: out.message, ...(out.line ? { line: out.line } : {}) },
          });
        const host = box.current;
        if (!host) return;
        const root = (shadow.current ??= host.attachShadow({ mode: 'open' }));
        const style = document.createElement('style');
        style.textContent = SHADOW_CSS;
        root.replaceChildren(style, out.fragment);
        setState({ phase: 'done', out });
      });
    return () => ac.abort();
  }, [key, height]);

  const done = state.phase === 'done' ? state.out : null;
  return (
    <figure
      data-testid={testid}
      data-state={state.phase}
      data-render-ms={done ? String(done.hostMs) : undefined}
      data-elements={done ? String(done.elements) : undefined}
      data-removed={done ? done.removed.join(' ') : undefined}
      className={bare ? undefined : 'rounded-lg border border-divider bg-card p-3'}
    >
      <figcaption
        style={bare ? { display: 'none' } : undefined}
        className="mb-2 flex items-center gap-1.5 text-sm font-medium text-page-title"
      >
        <Sparkles className="h-4 w-4 text-nav-active" />
        <span className="truncate">{title}</span>
        {state.phase === 'rendering' && (
          <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-page-muted" />
        )}
      </figcaption>
      <div
        ref={box}
        data-testid={`${testid}-svg`}
        className="w-full text-page-text"
        style={{
          maxHeight: height ? height + 40 : undefined,
          overflow: 'auto',
          display: state.phase === 'error' ? 'none' : undefined,
        }}
      />
      {state.phase === 'error' && (
        <div
          data-testid={`${testid}-error`}
          data-kind={state.error.kind}
          className="rounded-md border border-red-500/40 bg-red-500/5 p-3"
        >
          <div className="flex items-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" /> The visualization did not render
          </div>
          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap text-[11px] font-mono text-page-text">
            {state.error.message}
            {state.error.line ? ` (line ${state.error.line})` : ''}
          </pre>
          {onFix && (
            <button
              type="button"
              data-testid={`${testid}-fix`}
              onClick={() => onFix(fixMessage(title, state.error))}
              className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-md border border-divider bg-transparent px-2 py-1 text-xs text-page-text hover:bg-nav-active-bg"
            >
              <Wrench className="h-3.5 w-3.5" /> Fix it
            </button>
          )}
        </div>
      )}
    </figure>
  );
}
